import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { toast } from '../../../../utilities/appToast';
import UploadFile from "../../../General/UploadFile";
import {
  getLiquidacionPdf,
  getExportablesFacturaLiquidacionApi,
  getExportablesComprobanteDePagoApi,
  postComprobantesLiquidacionPdfApi,
} from "../../../../api/axios";
import { fetchLiquidacionesBusquedaDirecto } from '../../../../actions/LiquidacionesActions';
import { fechaLimite, filtroHoy } from "../../../../utilities/fechasFiltro";
import '../../shared/liquidacionesModule.css';
import LiquidacionesPageHeader from '../../shared/LiquidacionesPageHeader';
import { LiquidacionesBtnGuardar } from '../../shared/LiquidacionesButtons';
import { LIQUIDACIONES_ROUTES } from '../../shared/liquidacionesRoutes';

function archivoPdfABase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Lectura inválida"));
        return;
      }
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}

function normalizarPayloadApi(data) {
  if (data == null || data === "") return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

/** Errores API: array de strings o de { item1, item2 } (Swagger). */
function toastErroresLista(errs) {
  if (!Array.isArray(errs) || !errs.length) return false;
  errs.forEach((e) => {
    if (e && typeof e === "object" && ("item1" in e || "item2" in e)) {
      const msg = [e.item1, e.item2].filter(Boolean).join(" — ");
      if (msg) toast.error(msg);
    } else if (typeof e === "string") toast.error(e);
    else toast.error(String(e));
  });
  return true;
}

/** 400 o cuerpo erróneo: array [{ item1, item2 }], { errores }, o string JSON. */
function toastErroresRespuesta(err) {
  const data = normalizarPayloadApi(err?.response?.data);
  if (Array.isArray(data) && data.length > 0) return toastErroresLista(data);
  if (data && typeof data === "object" && !Array.isArray(data) && data.errores) {
    return toastErroresLista(data.errores);
  }
  return false;
}

function descargarPdfBase64(base64, nombreArchivo) {
  const cleanBase64 = base64.replace(/^data:application\/pdf;base64,/, "");
  try {
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = nombreArchivo || `Liquidacion_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    toast.error("No se pudo generar el PDF");
  }
}

function ComprobantesLayout({ title, description, children }) {
  return (
    <div className="container gestion-liquidaciones liquidaciones-modulo py-3">
      <LiquidacionesPageHeader
        title={title}
        subtitle={description}
        backTo={LIQUIDACIONES_ROUTES.liquidaciones}
        backLabel="Volver a liquidaciones"
        showBack
      />
      {children}
    </div>
  );
}

function ComprobantesAdmin() {
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const onFileLoaded = (file) => {
    setArchivo(file && file !== "" ? file : null);
  };

  const subirPdf = async () => {
    if (!archivo) {
      toast.warn("Seleccione un archivo PDF");
      return;
    }
    setEnviando(true);
    try {
      const base64 = await archivoPdfABase64(archivo);
      const response = await postComprobantesLiquidacionPdfApi({ archivo: base64 });
      const data = normalizarPayloadApi(response?.data);
      if (Array.isArray(data) && data.length > 0) {
        toastErroresLista(data);
        return;
      }
      toast.success("Archivo subido correctamente");
      setArchivo(null);
    } catch (err) {
      if (!toastErroresRespuesta(err)) {
        toast.error(err?.response?.data?.mensaje || "No se pudo subir el archivo");
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <ComprobantesLayout
      title="Comprobantes"
      description="Subí y administrá los comprobantes de liquidación desde un único punto."
    >
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">
          <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
            Carga transferencias interbanking
          </h6>
          <p className="text-secondary small mb-4">
            Arrastrá un PDF aquí o hacé clic en el ícono para elegir el archivo. Formatos aceptados: PDF.
          </p>
          <div className="row align-items-end">
            <div className="col-12 col-md-auto mb-3 mb-md-0">
              <UploadFile inputId="comprobantesLiquidacionPdf" acceptType=".pdf" onFileLoaded={onFileLoaded} />
            </div>
            <div className="col-12 col-md-auto">
              <LiquidacionesBtnGuardar
                type="button"
                disabled={enviando || !archivo}
                onClick={subirPdf}
              >
                {enviando ? "Subiendo…" : "Subir PDF"}
              </LiquidacionesBtnGuardar>
            </div>
          </div>
        </div>
      </div>
    </ComprobantesLayout>
  );
}

function ComprobantesSeller({ idSeller }) {
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [idSeleccion, setIdSeleccion] = useState("");
  const [cargandoLista, setCargandoLista] = useState(true);
  const [descargando, setDescargando] = useState(false);

  const cargarLista = useCallback(async () => {
    if (!idSeller) {
      setCargandoLista(false);
      return;
    }
    setCargandoLista(true);
    try {
      const res = await fetchLiquidacionesBusquedaDirecto({
        fechaDesde: fechaLimite(),
        fechaHasta: filtroHoy(),
        idEstado: 3,
        idSeller,
      });
      const lista = Array.isArray(res?.encabezado) ? res.encabezado : [];
      setLiquidaciones(lista);
    } catch (err) {
      toast.error("No se pudo cargar el listado de liquidaciones");
      setLiquidaciones([]);
    } finally {
      setCargandoLista(false);
    }
  }, [idSeller]);

  useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  const idLiquidacionNum = idSeleccion ? parseInt(idSeleccion, 10) : 0;

  const liquidacionSeleccionada = liquidaciones.find(
    (li) => Number(li?.idLiquidacion) === idLiquidacionNum
  );
  const idSellerComprobante = liquidacionSeleccionada?.idSeller ?? idSeller;

  const descargarDetalleLiquidacion = async () => {
    if (!idLiquidacionNum) {
      toast.warn("Seleccione una liquidación");
      return;
    }
    setDescargando(true);
    try {
      const response = await getLiquidacionPdf({ idLiquidacion: idLiquidacionNum });
      const pdf = response?.data?.pdf;
      if (pdf) {
        descargarPdfBase64(pdf, `Detalle_liquidacion_${idLiquidacionNum}.pdf`);
        toast.success("Descarga iniciada");
      } else {
        toast.error("No se recibió el PDF");
      }
    } catch (err) {
      err?.response?.data?.errores?.forEach?.((e) => toast.error(e));
      if (!err?.response?.data?.errores?.length) toast.error("No pudo descargarse el detalle de liquidación");
    } finally {
      setDescargando(false);
    }
  };

  const descargarFacturaLiquidacion = async () => {
    if (!idLiquidacionNum) {
      toast.warn("Seleccione una liquidación");
      return;
    }
    setDescargando(true);
    try {
      const response = await getExportablesFacturaLiquidacionApi({ idLiquidacion: idLiquidacionNum });
      const data = normalizarPayloadApi(response?.data);
      if (Array.isArray(data)) {
        if (data.length) toastErroresLista(data);
        else toast.error("No se recibió la factura");
        return;
      }
      const errs = data?.errores;
      if (Array.isArray(errs) && errs.length) toastErroresLista(errs);
      const pdf = data?.archivo;
      if (pdf && String(pdf).trim()) {
        descargarPdfBase64(pdf, `Factura_liquidacion_${idLiquidacionNum}.pdf`);
        toast.success("Descarga iniciada");
      } else if (!errs?.length) {
        toast.error("No se recibió la factura");
      }
    } catch (err) {
      if (!toastErroresRespuesta(err)) {
        toast.error("No se pudo descargar la factura de liquidación");
      }
    } finally {
      setDescargando(false);
    }
  };

  const descargarComprobantePago = async () => {
    if (!idLiquidacionNum) {
      toast.warn("Seleccione una liquidación");
      return;
    }
    setDescargando(true);
    try {
      const response = await getExportablesComprobanteDePagoApi({ idSeller: idSellerComprobante });
      const data = normalizarPayloadApi(response?.data);
      if (Array.isArray(data)) {
        if (data.length) toastErroresLista(data);
        else toast.error("No se recibió el PDF");
        return;
      }
      const errs = data?.errores;
      if (Array.isArray(errs) && errs.length) toastErroresLista(errs);
      const pdf = data?.archivo;
      if (pdf && String(pdf).trim()) {
        descargarPdfBase64(pdf, `Comprobante_pago_seller_${idSellerComprobante}.pdf`);
        toast.success("Descarga iniciada");
      } else if (!errs?.length) {
        toast.error("No se recibió el PDF");
      }
    } catch (err) {
      if (!toastErroresRespuesta(err)) {
        toast.error("No se pudo descargar el comprobante de pago");
      }
    } finally {
      setDescargando(false);
    }
  };

  if (!idSeller) {
    return (
      <div className="container py-3">
        <div className="alert alert-warning">No se identificó el seller para esta sesión.</div>
      </div>
    );
  }

  const descargaLiquidacionActiva = Boolean(idLiquidacionNum) && !descargando;

  return (
    <ComprobantesLayout
      title="Comprobantes"
      description="Seleccioná una liquidación y descargá la documentación disponible para el seller."
    >
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">
          <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
            Descargas
          </h6>
          <p className="text-secondary small mb-4">
            Elija la liquidación y descargue la documentación disponible.
          </p>

          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-5">
              <label className="form-label small text-secondary mb-1">Liquidación</label>
              <select
                className="form-control form-control-sm"
                value={idSeleccion}
                onChange={(e) => setIdSeleccion(e.target.value)}
                disabled={cargandoLista}
              >
                <option value="">{cargandoLista ? "Cargando…" : "Seleccione liquidación"}</option>
                {liquidaciones.map((li) => (
                  <option key={li.idLiquidacion} value={li.idLiquidacion}>
                    N° {li.idLiquidacion} — {li.mostrarFecha ?? ""} — {li.razonSocialSeller ?? ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row flex-wrap gap-2">
            <button
              type="button"
              className={`btn btn-sm d-inline-flex align-items-center ${descargaLiquidacionActiva ? "gestion-liquidaciones-btn-guardar" : "gestion-liquidaciones-btn-limpiar"}`}
              disabled={descargando || !idLiquidacionNum}
              onClick={descargarDetalleLiquidacion}
            >
              <i className="mdi mdi-download me-1 flex-shrink-0" style={{ fontSize: "0.875rem" }} aria-hidden />
              Descarga detalle liquidación
            </button>
            <button
              type="button"
              className={`btn btn-sm d-inline-flex align-items-center ${descargaLiquidacionActiva ? "gestion-liquidaciones-btn-guardar" : "gestion-liquidaciones-btn-limpiar"}`}
              disabled={descargando || !idLiquidacionNum}
              onClick={descargarFacturaLiquidacion}
            >
              <i className="mdi mdi-download me-1 flex-shrink-0" style={{ fontSize: "0.875rem" }} aria-hidden />
              Descargar factura de liquidación
            </button>
            <button
              type="button"
              className={`btn btn-sm d-inline-flex align-items-center ${descargaLiquidacionActiva ? "gestion-liquidaciones-btn-guardar" : "gestion-liquidaciones-btn-limpiar"}`}
              disabled={descargando || !idLiquidacionNum}
              onClick={descargarComprobantePago}
            >
              <i className="mdi mdi-download me-1 flex-shrink-0" style={{ fontSize: "0.875rem" }} aria-hidden />
              Descargar comprobante de pago
            </button>
            <button
              type="button"
              className="btn btn-sm gestion-liquidaciones-btn-limpiar d-inline-flex align-items-center"
              disabled
              title="Próximamente"
            >
              <i className="mdi mdi-download me-1 flex-shrink-0" style={{ fontSize: "0.875rem" }} aria-hidden />
              Descarga de certificados
            </button>
          </div>
        </div>
      </div>
    </ComprobantesLayout>
  );
}

function ComprobantesLiquidaciones({ userType, idSeller }) {
  const esAdminOCategory = userType?.isCategory || userType?.isAdminMod;
  const esSeller = userType?.isSellerAdmin || userType?.isSeller;

  if (esAdminOCategory) {
    return <ComprobantesAdmin />;
  }
  if (esSeller) {
    return <ComprobantesSeller idSeller={idSeller} />;
  }

  return (
    <div className="container py-3">
      <div className="alert alert-warning mb-0">No tiene permisos para acceder a esta sección.</div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  userType: state.general.userType,
  idSeller: state.general.idSeller,
});

export default connect(mapStateToProps)(ComprobantesLiquidaciones);
